import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, FileText, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Required, non-dismissable: customer must give at least a house number + base
export const AddressGate = ({ open, onSaved }) => {
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const value = address.trim();
    if (value.length < 3) { toast.error("Please enter your house number and base, e.g. 3725 Camp Foster"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/customers/my-profile`, { address: value }, { withCredentials: true });
      toast.success("Address saved");
      onSaved(value);
    } catch {
      toast.error("Couldn't save address. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent data-testid="address-gate-modal" className="bg-[#0A0A0A] border-[#39FF14]/40 text-white max-w-md [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <div className="w-12 h-12 rounded-xl bg-[#39FF14] flex items-center justify-center mb-2">
          <MapPin className="w-6 h-6 text-black" />
        </div>
        <DialogTitle className="font-heading text-2xl uppercase">Where's your yard?</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">
          We need your address before we can quote or cut. It doesn't have to be complete, just your house number and base.
        </DialogDescription>
        <form onSubmit={save} className="space-y-3 mt-2">
          <Input data-testid="address-gate-input" autoFocus value={address} onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g. 3725 Camp Foster" className="h-12 text-base bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]" />
          <Button data-testid="address-gate-save-btn" type="submit" disabled={saving}
            className="w-full h-12 bg-[#39FF14] text-black hover:bg-[#32D711] font-bold uppercase tracking-wider">
            {saving ? "Saving..." : "Save Address"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const QuotePromptDialog = ({ open, onClose, onStart }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="quote-prompt-modal" className="bg-[#0A0A0A] border-[#39FF14]/40 text-white max-w-md">
      <div className="w-12 h-12 rounded-xl bg-[#FACC15] flex items-center justify-center mb-2">
        <FileText className="w-6 h-6 text-black" />
      </div>
      <DialogTitle className="font-heading text-2xl uppercase">One more step</DialogTitle>
      <DialogDescription className="text-[#A1A1AA]">
        To get your price, submit a quote request with your address. We'll review your yard and send you a quote.
      </DialogDescription>
      <div className="flex flex-col gap-2 mt-2">
        <Button data-testid="quote-prompt-start-btn" onClick={onStart}
          className="h-12 bg-[#39FF14] text-black hover:bg-[#32D711] font-bold uppercase tracking-wider">
          Request My Quote <ChevronRight className="w-5 h-5 ml-1" />
        </Button>
        <Button data-testid="quote-prompt-later-btn" variant="ghost" onClick={onClose} className="text-[#A1A1AA] hover:text-white hover:bg-[#121212]">
          Maybe later
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
